import React from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button
} from "@heroui/react";
import { XMarkIcon, VideoCameraIcon } from "@heroicons/react/24/outline";
import { useUploadModal } from "../contexts/UploadModalContext";
import VideoUpload from "../videos/VideoUploadHeroUI";

const UploadModalHeroUI = () => {
  const { showUploadModal, closeUploadModal } = useUploadModal();

  // Enhanced mobile detection
  const isMobile =
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
    ('ontouchstart' in window) ||
    (window.innerWidth <= 768);

  // On mobile, render as a NORMAL PAGE (not fixed, not modal)
  // This is critical for iOS Safari keyboard behavior:
  // - No position:fixed (no stacking context trap)
  // - No overflow containers (no scroll-lock)
  // - Body scrolls naturally (like a regular page)
  if (isMobile && showUploadModal) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-full max-w-2xl p-4 pb-8">
          {/* Header */}
          <div className="flex flex-col gap-2 pb-4">
            <div className="flex items-center justify-between w-full">
              <h2 className="text-xl sm:text-2xl font-bold">Upload Video</h2>
              <Button
                isIconOnly
                variant="light"
                size="md"
                onPress={closeUploadModal}
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-xs sm:text-sm text-default-500">
              Share your automotive moments
            </p>
          </div>
          <VideoUpload />
        </div>
      </div>
    );
  }

  // Desktop: use normal Modal
  return (
    <Modal
      isOpen={showUploadModal}
      onClose={closeUploadModal}
      size="2xl"
      backdrop="opaque"
      scrollBehavior="inside"
      placement="center"
      hideCloseButton={true}
      isDismissable={true}
      isKeyboardDismissDisabled={false}
      aria-labelledby="upload-modal-title"
      aria-describedby="upload-modal-description"
      classNames={{
        // Professional backdrop with subtle blur effect
        backdrop: [
          "bg-black/50 dark:bg-black/70",
          "backdrop-blur-sm",
          "supports-[backdrop-filter]:backdrop-blur-sm"
        ].join(" "),
        // Responsive wrapper with progressive spacing
        wrapper: [
          "p-4 sm:p-6",
          "flex items-center justify-center",
          "!min-h-screen"
        ].join(" "),
        // Professional modal base - clean rounded design
        base: [
          "bg-content2 dark:bg-content1",
          "text-foreground",
          "border-none",
          "shadow-2xl",
          "rounded-xl sm:rounded-3xl",
          "backdrop-blur-sm",
          "mx-4 my-0 sm:mx-auto sm:my-4",
          "max-h-[80vh] sm:max-h-[90vh]",
          "w-full sm:w-auto",
          "overflow-hidden",
          // Enhanced focus ring for accessibility
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        ].join(" "),
        // Professional header with improved spacing
        header: [
          "bg-content2 dark:bg-content1",
          "border-none",
          "text-foreground",
          "px-3 py-3 sm:px-8 sm:py-6",
          "sticky top-0 z-10"
        ].join(" "),
        // Professional body with better spacing
        body: [
          "p-0",
          "bg-content2 dark:bg-content1",
          "text-foreground"
        ].join(" "),
      }}
      motionProps={{
        variants: {
          enter: {
            y: 0,
            opacity: 1,
            scale: 1,
            transition: {
              duration: 0.4,
              ease: [0.4, 0, 0.2, 1],
            },
          },
          exit: {
            y: 20,
            opacity: 0,
            scale: 0.96,
            transition: {
              duration: 0.3,
              ease: [0.4, 0, 1, 1],
            },
          },
        },
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary flex items-center justify-center shadow-md">
                    <VideoCameraIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground" />
                  </div>
                  <div className="space-y-0.5 sm:space-y-1">
                    <h2 id="upload-modal-title" className="text-lg sm:text-2xl font-semibold text-foreground leading-tight">
                      Upload Your Experience
                    </h2>
                    <p id="upload-modal-description" className="text-xs sm:text-sm text-foreground/70 font-normal leading-relaxed">
                      Share your vehicle experience with the community
                    </p>
                  </div>
                </div>
                <Button
                  isIconOnly
                  variant="light"
                  onPress={closeUploadModal}
                  className={[
                    "min-w-8 min-h-8 sm:min-w-10 sm:min-h-10",
                    "text-default-500",
                    "hover:text-foreground",
                    "hover:bg-default-100",
                    // Only show focus ring on keyboard navigation, not touch/click
                    "focus-visible:outline-none",
                    isMobile ? "" : "focus-visible:ring-2 focus-visible:ring-focus",
                    "transition-colors duration-200",
                    // Remove tap highlight on mobile
                    "tap-highlight-transparent"
                  ].join(" ")}
                  aria-label="Close upload modal"
                  disableRipple={isMobile}
                >
                  <XMarkIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </div>
            </ModalHeader>
            <ModalBody>
              <div className="px-3 pb-4 sm:px-8 sm:pb-8 space-y-3 sm:space-y-6">
                <VideoUpload onUploadSuccess={closeUploadModal} />
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default UploadModalHeroUI;
